"use client";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useMemo, useState } from "react";
import { useSignals } from "@preact/signals-react/runtime";
import {
  eventInSingleEventPage,
  loadEventById,
  ticketPriceRange,
} from "@/app/events/store/single-page.store";
import {
  extractUtcOffset,
  formatedDateTimeOffsetUtc,
} from "@/lib/date-time-utils";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TicketSelector } from "@/app/events/[id]/components/TicketSelector";
import {
  clearAllData,
  getTicketDialogVisibility,
  isGetTicketDialogOpen,
  loadEventTickets,
} from "../store/buy-tickets.store";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { EventStatus } from "@/app/dashboard/models/Event";
import { ClockAlert, TriangleAlert } from "lucide-react";
import React from "react";
import { useParams, useSearchParams } from "next/navigation";

export const RightSideCard = () => {
  const { id } = useParams();
  const searchParams = useSearchParams();
  const previewToken = searchParams.get("t") ?? null;
  const [isOpen, setIsOpen] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const event = eventInSingleEventPage.value;
  const isSoldOut = useMemo(
    () => event?.status === EventStatus.SoldOut,
    [event?.status]
  );
  const ticketSalesClosed = Boolean(event?.isTicketSalesClosed);
  const ticketsUnavailable = isSoldOut || ticketSalesClosed;

  useSignals();

  useEffect(() => {
    clearAllData();
    loadEventById(id as string, previewToken);
    loadEventTickets(id as string);
  }, [id, previewToken]);

  return (
    <>
      <Card className="border border-stone-neutral-4 rounded-none lg:rounded-lg">
        <CardContent className="p-4 flex lg:block">
          <div className="hidden lg:block">
            <p className="text-lg text-center lg:text-left font-bold text-ocean-blue-9">
              {event?.name}
            </p>
            <div className="mt-2">
              <p className="text-base text-center lg:text-left text-stone-neutral-11 pb-0">
                Venue
              </p>
              <p className="text-md text-center lg:text-left font-medium">
                {event?.location?.name}
              </p>
            </div>
            <div className="mt-2">
              <p className="text-base text-center lg:text-left text-stone-neutral-11 pb-0">
                Date
              </p>
              <p className="text-md text-center lg:text-left font-medium">
                {event?.startTimeStamp &&
                  event?.location?.timeZone &&
                  formatedDateTimeOffsetUtc(
                    event.startTimeStamp,
                    extractUtcOffset(event.location.timeZone),
                    "dddd, MMMM D YYYY [at] h:mm A"
                  )}
              </p>
            </div>
            <div className="mt-2">
              <p className="text-base text-center lg:text-left text-stone-neutral-11 pb-0">
                Tickets
              </p>
              <p className="text-md text-center lg:text-left font-medium pb-3">
                {ticketPriceRange.value}
              </p>
            </div>
            {ticketsUnavailable && (
              <div className="flex items-center py-1 px-2 rounded-lg bg-lemon-yellow-2 border border-lemon-yellow-3 mb-3">
                {isSoldOut ? (
                  <>
                    <TriangleAlert
                      strokeWidth={1.5}
                      size={20}
                      className="stroke-lemon-yellow-12"
                    />
                    <div className="text-base text-lemon-yellow-12 ml-1">
                      Tickets sold out
                    </div>
                  </>
                ) : (
                  <>
                    <ClockAlert
                      strokeWidth={1.5}
                      size={20}
                      className="stroke-lemon-yellow-12"
                    />
                    <div className="text-base text-lemon-yellow-12 ml-1">
                      Ticket sales have closed.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div
            className={`lg:hidden flex justify-start items-center w-full ${
              isSoldOut ? "flex-col" : "flex-row"
            }`}
          >
            <div className="flex gap-1 w-full">
              <p className="text-base text-stone-neutral-11 m-0">Tickets:</p>
              <p className="text-base font-semibold text-stone-neutral-12 m-0">
                {ticketPriceRange.value}
              </p>
            </div>
            {ticketsUnavailable && (
              <div className="flex items-center py-1 px-2 rounded-lg bg-lemon-yellow-2 border border-lemon-yellow-3 mb-3">
                {isSoldOut ? (
                  <>
                    <TriangleAlert
                      strokeWidth={1.5}
                      size={20}
                      className="stroke-lemon-yellow-12"
                    />
                    <div className="text-base text-lemon-yellow-12 ml-1">
                      Tickets sold out
                    </div>
                  </>
                ) : (
                  <>
                    <ClockAlert
                      strokeWidth={1.5}
                      size={20}
                      className="stroke-lemon-yellow-12"
                    />
                    <div className="text-base text-lemon-yellow-12 ml-1">
                      Ticket sales have closed.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          {!ticketsUnavailable && (
            <div className="flex gap-2">
              <Dialog
                open={isGetTicketDialogOpen.value}
                onOpenChange={getTicketDialogVisibility}
              >
                <DialogTrigger asChild>
                  <Button className="flex-1">Get tickets</Button>
                </DialogTrigger>
                <DialogContent className="max-h-[95vh] w-[95vw] sm:w-[80vw] max-w-[1200px]">
                  <VisuallyHidden.Root>
                    <DialogTitle>Dialog Title</DialogTitle>
                  </VisuallyHidden.Root>
                  <TicketSelector />
                </DialogContent>
              </Dialog>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
